/**
 * Renders the same configuration table used on each repeatable step, for a
 * location-filtered unit subset (#999 Devices view).
 *
 * Story 3.7: row press opens the unit editor; optional shared reorder writes
 * through via onReorderSection while Add/menus stay read-only.
 * Story 3.9: DevicesTableMode + step list registry (no ×6 copy-paste).
 */

import React, { useCallback } from 'react';

import { renderDevicesStepList } from './devicesStepListRegistry';
import { buildDevicesTableListProps } from './devicesTableMode';

import type { DeviceSourceUnit } from '../../utils/deviceRowMapper';

export type DevicesReorderHandler = (
  fromIndex: number,
  toIndex: number,
) => void;

export interface LocationViewStepListProps {
  stepId: string;
  units: readonly unknown[];
  testIDPrefix: string;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  /** When set, row press opens that unit's editor (Devices view). */
  onUnitPress?: (unit: DeviceSourceUnit) => void;
  /** When set with editing rights, enables shared reorder (Story 3.7). */
  onReorderUnits?: DevicesReorderHandler;
}

export const LocationViewStepList: React.FC<LocationViewStepListProps> = ({
  stepId,
  units,
  testIDPrefix,
  resolveLocationTagName,
  onUnitPress,
  onReorderUnits,
}) => {
  const noopPress = useCallback((_unit: unknown) => {}, []);
  const noopAdd = useCallback(() => {}, []);
  const onPress = onUnitPress ?? noopPress;
  const allowReorder = Boolean(onReorderUnits);

  const onReorderSection = useCallback(
    (_section: 'existing' | 'new', fromIndex: number, toIndex: number) => {
      onReorderUnits?.(fromIndex, toIndex);
    },
    [onReorderUnits],
  );

  const shared = {
    ...buildDevicesTableListProps({
      allowReorder,
      onReorderSection: allowReorder ? onReorderSection : undefined,
      disabled: !onUnitPress && !allowReorder,
      resolveLocationTagName,
    }),
    testID: `${testIDPrefix}-${stepId}-list`,
  };

  return renderDevicesStepList(stepId, {
    units,
    onPress: onPress as (unit: DeviceSourceUnit) => void,
    onAdd: noopAdd,
    shared,
  });
};
